// ═══════════════════════════════════════════════════════════
// Analytics & Reporting Dashboard
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { IconLoader2, IconTrendingUp, IconAlertCircle, IconCheck, IconClock } from '@tabler/icons-react';
import { useRequestStore } from '../../../store/useRequestStore';

import { listDepartments } from '../../../api/departments';
import type { FoiaRequest } from '../../../api/requests';

const STATUS_LABELS: Record<string, string> = {
  submitted: 'Submitted', acknowledged: 'Acknowledged', inProgress: 'In Progress',
  awaitingRequester: 'Awaiting Requester', awaitingPayment: 'Awaiting Payment',
  pendingReview: 'Pending Review', completed: 'Completed', denied: 'Denied',
  withdrawn: 'Withdrawn', onHold: 'On Hold', draft: 'Draft',
};

const STATUS_COLORS: Record<string, string> = {
  submitted: '#3B82F6', acknowledged: 'var(--cds-green-700)', inProgress: 'var(--color-primary-700)',
  awaitingRequester: 'var(--cds-yellow-500)', awaitingPayment: 'var(--cds-yellow-500)', pendingReview: '#8B5CF6',
  completed: '#059669', denied: 'var(--cds-red-700)', withdrawn: 'var(--color-neutral-500)',
  onHold: 'var(--color-neutral-500)', draft: '#94A3B8',
};

function StatCard({
  label, value, sub, icon: Icon, color,
}: { label: string; value: string | number; sub?: string; icon: React.ComponentType<{ size: number; style?: React.CSSProperties }>; color: string }) {
  return (
    <div className="card" style={{ padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-500)' }}>{label}</span>
        <Icon size={18} style={{ color }} />
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--color-neutral-900)' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function BarChart({ data }: { data: { label: string; value: number; color?: string }[] }) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map(d => (
        <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 130, fontSize: 12, color: 'var(--color-neutral-600)', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</div>
          <div style={{ flex: 1, height: 20, background: 'var(--color-neutral-100)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${(d.value / max) * 100}%`,
              background: d.color ?? 'var(--color-primary-500)',
              borderRadius: 4, minWidth: d.value > 0 ? 4 : 0,
              transition: 'width 0.3s ease',
            }} />
          </div>
          <div style={{ width: 28, fontSize: 12, fontWeight: 600, color: 'var(--color-neutral-700)', flexShrink: 0, textAlign: 'right' }}>{d.value}</div>
        </div>
      ))}
    </div>
  );
}

function computeStats(requests: FoiaRequest[]) {
  const all   = requests;
  const open  = all.filter(r => !['completed', 'denied', 'withdrawn'].includes(r.status));
  const closed = all.filter(r => ['completed', 'denied', 'withdrawn'].includes(r.status));
  const overdue = open.filter(r => r.isOverdue);
  const dueSoon = open.filter(r => !r.isOverdue && !r.isPaused && (r.daysRemaining ?? 99) <= 5);
  const paused  = open.filter(r => r.isPaused);

  // Status breakdown
  const statusCounts: Record<string, number> = {};
  for (const r of all) {
    statusCounts[r.status] = (statusCounts[r.status] ?? 0) + 1;
  }

  // By department
  const deptCounts: Record<string, number> = {};
  for (const r of all) {
    const dept = r.assignedDeptId || 'Unassigned';
    deptCounts[dept] = (deptCounts[dept] ?? 0) + 1;
  }

  // By channel
  const channelCounts: Record<string, number> = {};
  for (const r of all) {
    channelCounts[r.requestChannel] = (channelCounts[r.requestChannel] ?? 0) + 1;
  }

  // By month (last 6 months)
  const monthlyCounts: Record<string, number> = {};
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    monthlyCounts[key] = 0;
  }
  for (const r of all) {
    if (!r.dateSubmitted) continue;
    const d = new Date(r.dateSubmitted);
    const key = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    if (key in monthlyCounts) monthlyCounts[key]++;
  }

  // Avg days to close
  let totalDays = 0, closedCount = 0;
  for (const r of closed) {
    if (r.dateSubmitted && r.closedAt) {
      const days = Math.floor((new Date(r.closedAt).getTime() - new Date(r.dateSubmitted).getTime()) / 86400000);
      if (days >= 0) { totalDays += days; closedCount++; }
    }
  }
  const avgDaysToClose = closedCount > 0 ? Math.round(totalDays / closedCount) : 0;

  // On-time rate (closed within statutory window)
  const onTime = closed.filter(r => !r.isOverdue).length;
  const onTimeRate = closed.length > 0 ? Math.round((onTime / closed.length) * 100) : 100;

  return {
    total: all.length, openCount: open.length, closedCount: closed.length,
    overdue: overdue.length, dueSoon: dueSoon.length, paused: paused.length,
    statusCounts, deptCounts, channelCounts, monthlyCounts,
    avgDaysToClose, onTimeRate,
  };
}

export default function Analytics() {
  const requests  = useRequestStore(s => s.requests);
  const loading   = useRequestStore(s => s.loading);
  const fetchRequests = useRequestStore(s => s.fetchRequests);
  const [deptMap, setDeptMap] = useState<Record<string, string>>({});
  const fetched = useRef(false);

  useEffect(() => {
    listDepartments().then(depts => {
      const map: Record<string, string> = {};
      for (const d of depts) map[String(d.recordId)] = d.name;
      setDeptMap(map);
    });
    if (fetched.current) return;
    fetched.current = true;
    fetchRequests({ includeClosd: true });
  }, [fetchRequests]);

  const stats = computeStats(requests);

  if (loading && requests.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-neutral-400)' }}>
        <IconLoader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  const statusBarData = Object.entries(stats.statusCounts)
    .map(([status, count]) => ({
      label: STATUS_LABELS[status] ?? status,
      value: count,
      color: STATUS_COLORS[status],
    }))
    .sort((a, b) => b.value - a.value);

  const deptBarData = Object.entries(stats.deptCounts)
    .map(([id, count]) => ({
      label: id === 'Unassigned' ? '— Unassigned' : (deptMap[id] ?? id),
      value: count,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10);

  const channelBarData = Object.entries(stats.channelCounts)
    .map(([ch, count]) => ({ label: ch, value: count }))
    .sort((a, b) => b.value - a.value);

  const monthlyBarData = Object.entries(stats.monthlyCounts)
    .map(([month, count]) => ({ label: month, value: count, color: 'var(--color-primary-500)' }));

  return (
    <div style={{ padding: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-neutral-900)', margin: 0 }}>
          Analytics &amp; Reporting
        </h1>
      </div>

      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Requests"  value={stats.total}         sub={`${stats.openCount} open · ${stats.closedCount} closed`} icon={IconTrendingUp} color="var(--color-primary-700)" />
        <StatCard label="Overdue"         value={stats.overdue}       sub={`${stats.dueSoon} due soon`} icon={IconAlertCircle} color="var(--cds-red-700)" />
        <StatCard label="Avg Days to Close" value={stats.avgDaysToClose} sub={`${stats.onTimeRate}% on-time rate`} icon={IconClock} color="var(--cds-yellow-500)" />
        <StatCard label="Clock Paused"    value={stats.paused}        sub="awaiting action" icon={IconCheck} color="var(--cds-green-700)" />
      </div>

      {/* Charts row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-500)', marginBottom: 14 }}>
            Submissions by Month
          </div>
          <BarChart data={monthlyBarData} />
        </div>
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-500)', marginBottom: 14 }}>
            Status Breakdown
          </div>
          <BarChart data={statusBarData} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-500)', marginBottom: 14 }}>
            Volume by Department
          </div>
          {deptBarData.length > 0 ? <BarChart data={deptBarData} /> : <div style={{ fontSize: 14, color: 'var(--color-neutral-400)' }}>No data</div>}
        </div>
        <div className="card" style={{ padding: '16px 20px' }}>
          <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-500)', marginBottom: 14 }}>
            Requests by Channel
          </div>
          <BarChart data={channelBarData} />
        </div>
      </div>
    </div>
  );
}
